import * as React from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import "./Slider.css";

// Fixed constants
// Updated Constants for each section
const INCOME_MIN = 30_000;
const INCOME_MAX = 1_000_000;

const PURCHASE_MIN = 200_000;
const PURCHASE_MAX = 10_000_000;

const DEPOSIT_MIN = 3_000;
const DEPOSIT_MAX = 1_000_000;

const EXPENSE_MIN = 1_000;
const EXPENSE_MAX = 100_000;

const STEP = 1_000; // Lower step for more precision
const MARK_INTERVAL = 100_000; // Reasonable interval for all

const getMarks = (min, max) => {
  const range = max - min;
  const approxSteps = 8; // Ideal number of marks visible
  const stepSize = Math.ceil(range / approxSteps / 1000) * 1000; // round to nearest 1000

  const marks = [];
  for (let value = min; value <= max; value += stepSize) {
    marks.push({
      value,
      label: value >= 1_000_000 ? `${value / 1_000_000}M` : `${value / 1_000}k`,
    });
  }

  // Ensure the last mark is exactly 'max'
  if (marks.length === 0 || marks[marks.length - 1].value !== max) {
    marks.push({
      value: max,
      label: max >= 1_000_000 ? `${max / 1_000_000}M` : `${max / 1_000}k`,
    });
  }

  return marks;
};

// Shared slider styles
const sliderStyle = {
  color: "#6D5DFC",
  height: 8,
  "& .MuiSlider-thumb": {
    height: 20,
    width: 20,
    backgroundColor: "#fff",
    border: "4px solid #6D5DFC",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    "&:hover": {
      boxShadow: "0px 6px 12px rgba(0,0,0,0.2)",
    },
  },
  "& .MuiSlider-track": { height: 8, borderRadius: 4 },
  "& .MuiSlider-rail": {
    height: 8,
    borderRadius: 4,
    background: "linear-gradient(to right, #c4c4c4, #dcdcdc)",
  },
  "& .MuiSlider-markLabel": {
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
};

// Reusable slider section
const SliderSection = React.memo(function SliderSection({
  label,
  values,
  setValues,
  min,
  max,
  marks,
  minLabel,
  maxLabel,
  secondaryLabel
}) {
  const [localMin, setLocalMin] = React.useState(values[0]);
  const [localMax, setLocalMax] = React.useState(values[1]);

  React.useEffect(() => {
    setLocalMin(values[0]);
    setLocalMax(values[1]);
  }, [values]);

  const handleLocalInputChange = (setter) => (e) => {
    const val = Math.min(parseInt(e.target.value.replace(/\D/g, ''), 10) || 0, max);
    setter(val);
  };

  const handleInputBlur = (index) => {
    let newArray = [localMin, localMax];

    // clamp to [min, max]
    newArray[0] = Math.max(min, Math.min(max, newArray[0]));
    newArray[1] = Math.max(min, Math.min(max, newArray[1]));

    // ensure min <= max
    if (newArray[0] > newArray[1]) {
      if (index === 0) newArray[1] = newArray[0];
      else newArray[0] = newArray[1];
    }

    // Prevent both ends at absolute minimum
    if (newArray[0] === newArray[1] && newArray[0] === min) {
      newArray[1] = Math.min(max, min + STEP);
    }

    setValues(newArray);
  };

  const handleSliderChange = (_, newValue) => {
    if (newValue[0] <= newValue[1]) {
      setValues([Math.max(min, newValue[0]), Math.min(max, newValue[1])]);
    }
  };

  return (
    <Box>
      <Typography className="question">{label}</Typography>
      {secondaryLabel && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
          {secondaryLabel}
        </Typography>
      )}

      <Slider
        value={values}
        onChange={handleSliderChange}
        valueLabelDisplay="on"
        min={min}
        max={max}
        marks={marks}
        step={STEP}
        sx={sliderStyle}
      />

      <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
        <TextField
          label={minLabel}
          value={localMin}
          onChange={handleLocalInputChange(setLocalMin)}
          onBlur={() => handleInputBlur(0)}
          onKeyDown={(e) => e.key === 'Enter' && handleInputBlur(0)}
          size="small"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 9 }}
        />
        <TextField
          label={maxLabel}
          value={localMax}
          onChange={handleLocalInputChange(setLocalMax)}
          onBlur={() => handleInputBlur(1)}
          onKeyDown={(e) => e.key === 'Enter' && handleInputBlur(1)}
          size="small"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 9 }}
        />
      </Box>
    </Box>
  );
});

export default function RangeSlider({
  purchasePrice,
  setPurchasePrice,
  income,
  setIncome,
  deposite,
  setDeposite,
  expenses,
  setExpenses,
}) {
  return (
    <Box sx={{ width: '100%', mx: 'auto', px: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <SliderSection
        label="What is your annual income?"
        values={income}
        setValues={setIncome}
        min={INCOME_MIN}
        max={INCOME_MAX}
        marks={getMarks(INCOME_MIN, INCOME_MAX)}
        minLabel="Min Income ($)"
        maxLabel="Max Income ($)"
      />

      <SliderSection
        label="What is your expected purchase price?"
        values={purchasePrice}
        setValues={setPurchasePrice}
        min={PURCHASE_MIN}
        max={PURCHASE_MAX}
        marks={getMarks(PURCHASE_MIN, PURCHASE_MAX)}
        minLabel="Min Price ($)"
        maxLabel="Max Price ($)"
      />

      <SingleInputSliderSection
        label="How much deposit do you have/saved?"
        value={deposite}
        setValue={setDeposite}
        min={DEPOSIT_MIN}
        max={DEPOSIT_MAX}
        marks={getMarks(DEPOSIT_MIN, DEPOSIT_MAX)}
        inputLabel="Deposit ($)"
      />


      <SliderSection
        label={
          <Box display="flex" alignItems="center">
            <Typography className="question" sx={{ mr: 1 }}>
              What are your monthly expenses?
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              (include food, rents, credit cards and loans etc)
            </Typography>
          </Box>
        }
        values={expenses}
        setValues={setExpenses}
        min={EXPENSE_MIN}
        max={EXPENSE_MAX}
        marks={getMarks(EXPENSE_MIN, EXPENSE_MAX)}
        minLabel="Min Expenses ($)"
        maxLabel="Max Expenses ($)"
      />
    </Box>
  );
}


const SingleInputSliderSection = React.memo(function SingleInputSliderSection({
  label,
  value = 0,
  setValue,
  min = 0,
  max = 3000,
  marks = [],
  inputLabel = 'Value',
}) {
  const [inputVal, setInputVal] = React.useState(String(value));

  React.useEffect(() => {
    setInputVal(String(value)); // Sync with external changes
  }, [value]);

  const handleSliderChange = (_, newValue) => {
    setValue(newValue);
  };

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    setInputVal(raw); // Allow temporary empty/invalid values
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputVal, 10);
    if (!isNaN(parsed)) {
      const bounded = Math.min(Math.max(parsed, min), max);
      console.log('bounded', bounded);
      setValue(bounded);
    } else {
      setInputVal(String(value)); // Reset if invalid
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInputBlur(); // Reuse same validation logic
    }
  };

  return (
    <Box>
      <Typography className="question">{label}</Typography>

      <Slider
        value={value}
        onChange={handleSliderChange}
        valueLabelDisplay="on"
        min={min}
        max={max}
        marks={marks}
        step={STEP}
        sx={sliderStyle}
      />

      <TextField
        label={inputLabel}
        value={inputVal}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        size="small"
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
          maxLength: 7,
        }}
        sx={{ mt: 2 }}
      />
    </Box>
  );
});
